import * as THREE from 'three'
import type RAPIER from '@dimforge/rapier3d-compat'
import type { PlayerController } from '../player'
import type { ProjectileManager } from '../weapons/projectiles'
import type { EnemyManager } from '../ai/enemy-manager'
import type { DestructibleManager } from '../world/destructibles'
import type { PickupManager } from '../world/pickups'
import type { SoundEffects } from '../audio/sfx'
import type { Scheduler } from '../engine/scheduler'
import { fireHitscan } from '../shooting'

export interface CombatConfig {
  player: PlayerController
  camera: THREE.PerspectiveCamera
  projectiles: ProjectileManager
  enemies: EnemyManager
  destructibles: DestructibleManager
  pickups: PickupManager
  targets: THREE.Mesh[]
  sfx?: SoundEffects
  scheduler: Scheduler
}

export class CombatCoordinator {
  private readonly player: PlayerController
  private readonly camera: THREE.PerspectiveCamera
  private readonly projectiles: ProjectileManager
  private readonly enemies: EnemyManager
  private readonly destructibles: DestructibleManager
  private readonly pickups: PickupManager
  private readonly targets: THREE.Mesh[]
  private readonly sfx?: SoundEffects
  private readonly scheduler: Scheduler

  private readonly muzzleDir = new THREE.Vector3()
  private readonly muzzlePos = new THREE.Vector3()

  constructor(config: CombatConfig) {
    this.player = config.player
    this.camera = config.camera
    this.projectiles = config.projectiles
    this.enemies = config.enemies
    this.destructibles = config.destructibles
    this.pickups = config.pickups
    this.targets = config.targets
    this.sfx = config.sfx
    this.scheduler = config.scheduler
  }

  tryFire(): boolean {
    if (!this.player.controls.isLocked) return false
    if (!this.player.weapons.consumeFiringCost(this.player.vitality)) return false

    const weapon = this.player.weapons.activeWeapon
    this.sfx?.playWeapon(weapon.id)

    // Alert nearby enemies of gunfire noise
    for (let e = 0; e < this.enemies.enemies.length; e++) {
      const en = this.enemies.enemies[e]
      if (en.alive && en.position.distanceTo(this.player.position) < 25) {
        en.ai.reportNoise(this.player.position)
      }
    }

    this.camera.getWorldDirection(this.muzzleDir)
    this.muzzlePos.copy(this.camera.position).addScaledVector(this.muzzleDir, 0.6)

    let homingTarget: THREE.Vector3 | null = null
    if (weapon.homing) {
      let closestDist = Infinity
      for (let e = 0; e < this.enemies.enemies.length; e++) {
        const en = this.enemies.enemies[e]
        if (en.alive) {
          const d = this.camera.position.distanceTo(en.position)
          if (d < closestDist) {
            closestDist = d
            homingTarget = en.position.clone()
          }
        }
      }
    }

    for (let p = 0; p < weapon.projectileCount; p++) {
      this.projectiles.spawn(weapon, this.muzzlePos, this.muzzleDir, homingTarget)
    }

    fireHitscan(this.camera, this.targets, this.scheduler)
    return true
  }

  updateProjectiles(dt: number, world: RAPIER.World) {
    this.projectiles.update(dt, world, (proj, hitPoint, hitCollider) => {
      // If enemy projectile hits player
      if (proj.definition.name === 'ENEMY BOLT') {
        if (hitPoint.distanceTo(this.player.position) < 1.4) {
          this.player.vitality.applyDamage(proj.definition.damage)
          this.sfx?.playPlayerHurt()
        }
        return
      }

      if (hitCollider) {
        // Check enemies
        for (let e = 0; e < this.enemies.enemies.length; e++) {
          const enemy = this.enemies.enemies[e]
          if (enemy.alive && enemy.collider === hitCollider) {
            this.sfx?.playImpact()
            const res = enemy.applyDamage(
              proj.definition.damage,
              proj.velocity,
              proj.definition.aoeRadius > 0,
            )
            if (res.died) {
              this.sfx?.playExplosion(2.0)
              this.pickups.spawn('energy-tank', enemy.position.clone().setY(0.6))
            }
            break
          }
        }

        // Check destructible crates
        for (let d = 0; d < this.destructibles.objects.length; d++) {
          const obj = this.destructibles.objects[d]
          if (obj.alive && obj.collider === hitCollider) {
            this.sfx?.playImpact()
            const res = this.destructibles.damageObject(obj, proj.definition.damage, world)
            if (res.destroyed) {
              this.sfx?.playExplosion(1.5)
            }
            break
          }
        }
      }
    })
  }

  spawnEnemyBolt(origin: THREE.Vector3, dir: THREE.Vector3, speed: number, damage: number, color: number) {
    this.projectiles.spawn(
      {
        id: 'basic2p',
        name: 'ENEMY BOLT',
        slot: 1,
        category: 'kinetic',
        ammoType: 'none',
        ammoCost: 0,
        fireRate: 1,
        damage,
        projectileSpeed: speed,
        projectileCount: 1,
        spreadAngle: 0.04,
        gravity: 0,
        bounces: 0,
        homing: false,
        penetrating: false,
        aoeRadius: 0,
        color,
        size: [0.15, 0.15, 0.6],
      },
      origin,
      dir,
    )
  }
}
